// src/rms/pages/Landing.js
// ============================================================================
// Public landing page
// ============================================================================
// Deliberately vertical-neutral: the pitch is "any rental business", and the
// visitor picks their trade on the next screen. Naming a specific vertical here
// would cost us the other eleven.
// ============================================================================

import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VERTICAL_PRESETS } from "../verticals/presets";
import LocalePicker from "../components/LocalePicker";
// The landing page is mounted outside RmsApp, so it imports the stylesheet
// itself. Webpack dedupes the second import.
import "../styles/rms.css";
import Brandmark from "../components/Brandmark";

/**
 * The seven promises on the landing page.
 *
 * Data rather than seven repeated JSX blocks, because every one of them needed
 * the same two translation keys and a hand-written card is a place to forget
 * one. The English lives here as the fallback, so the page still reads
 * correctly before any catalogue has been written.
 */
const FEATURES = [
  {
    key: "trade",
    title: "Speaks your trade",
    body: "Vehicles or vessels, tenants or customers, mileage or engine hours. Labels, fields and required steps all change with your business type.",
  },
  {
    key: "fields",
    title: "The right fields, already there",
    body: "VIN and fuel level for cars. Hull ID and engine hours for boats. Unit size and gate code for storage. No blank spreadsheet to design.",
  },
  {
    key: "checkout",
    title: "Checkout that can't go wrong",
    body: "Waivers, ID checks, deposits, condition photos and signatures are enforced where your trade needs them — and skipped where it doesn't.",
  },
  {
    key: "billing",
    title: "Billing both ways",
    body: "Invoice per rental, or bill month after month for continuously occupied storage units. Same ledger, same reports.",
  },
  {
    key: "conflicts",
    title: "Never double-booked",
    body: "Overlapping bookings on the same asset are rejected outright, whether they come from the counter, the calendar or an import.",
  },
  {
    key: "team",
    title: "Your whole team",
    body: "Staff accounts are free on every plan. No per-seat pricing, no counting heads at the front desk.",
  },
  {
    key: "data",
    title: "Your data, and it's always yours",
    body: "Download every customer, booking, agreement and invoice as one file, any time, on either plan — no request, no waiting. Close the account and it's deleted outright, not archived somewhere you can't reach.",
  },
  // Eighth, and not only because seven left a hole in the grid: thirteen
  // languages is real, finished work the pitch had never once mentioned.
  {
    key: "languages",
    title: "Thirteen languages, both directions",
    body: "The whole system, your staff's screens and the messages your customers receive — in their language, Arabic and Urdu included, laid out right to left.",
  },
];

/** The two plan comparison lists. Same reasoning as FEATURES. */
const FREE_PLAN = [
  "Unlimited assets and bookings",
  "Unlimited staff accounts",
  "Checkout, check-in and invoicing",
  "Full data export, any time",
  "Trade ads on browsing screens — never during a rental",
];

const AD_FREE_PLAN = [
  "Everything in Free",
  "No ads anywhere",
  "Nothing to load but your own data",
  "Priority support",
  "Cancel any time — and take your data with you",
];

function Landing() {
  // This page renders outside RmsProvider, so it translates through i18next
  // directly. There is no vertical here to have terminology for -- the pitch is
  // deliberately trade-neutral.
  const { t } = useTranslation();

  return (
    <div className="rms-root rms-landing">
      {/* Sticky: the way back and the way in should not require scrolling to
          the top to find them. The inner wrapper keeps the content aligned to
          the same 1080px column as the rest of the page while the bar itself
          spans the full width -- otherwise the border stops short of the
          edges. */}
      <header className="rms-landing-header">
        <div className="rms-landing-header-inner">
          <Brandmark size={31} style={{ padding: 0 }} />
          <nav className="rms-landing-nav">
            {/* Before the pitch, not after it: someone who cannot read this
                page needs the control first, not buried in a footer. */}
            <LocalePicker />
            <Link className="rms-btn rms-btn--ghost" to="/rms">
              {t("landing.signIn", "Sign in")}
            </Link>
            <Link className="rms-btn rms-btn--primary" to="/rms/start">
              {t("landing.startFree", "Start free")}
            </Link>
          </nav>
        </div>
      </header>

      <section className="rms-hero">
        <span className="rms-pill rms-pill--ok" style={{ marginBottom: 16 }}>
          {t("landing.badge", "Free forever · no card required")}
        </span>
        <h1>{t("landing.headline", "Run your whole rental business, free.")}</h1>
        <p>
          {t(
            "landing.subhead",
            "Reserve, check out, track and invoice — whether you rent cars, boats, tools, storage units or tuxedos. One system that speaks your trade's language, set up in about two minutes."
          )}
        </p>
        <div className="rms-hero-actions">
          <Link className="rms-btn rms-btn--primary" to="/rms/start" style={{ padding: "11px 22px" }}>
            {t("landing.startFree", "Start free")}
          </Link>
          <a className="rms-btn rms-btn--ghost" href="#how" style={{ padding: "11px 22px" }}>
            {t("landing.cta.how", "See how it works")}
          </a>
        </div>
        <p className="rms-hero-note">
          {t("landing.cta.sampleNote", "Every new account starts with sample data to look around in, and you can clear it in one click.")}
        </p>
      </section>

      <section className="rms-landing-section" style={{ paddingTop: 0 }}>
        <p style={{ textAlign: "center", fontSize: 12.5, color: "var(--rms-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 650 }}>
          {t("landing.verticalsLabel", "Configured for 12 rental verticals")}
        </p>
        <p style={{ textAlign: "center", fontSize: 13.5, color: "var(--rms-muted-strong)", margin: "0 auto 16px", maxWidth: 520 }}>
          {t("landing.verticalsHint", "Pick yours to start — we configure the fields, the vocabulary and the checkout steps around it.")}
        </p>
        {/* These were twelve grey labels. Each is now a way in: it carries the
            trade through to the picker already chosen, so nobody arrives at a
            grid of twelve and hunts for the one they just clicked. */}
        <div className="rms-vertical-chips">
          {VERTICAL_PRESETS.map((v) => (
            <Link
              key={v.key}
              className="rms-chip rms-chip--action"
              to={`/rms/start?vertical=${v.key}`}
            >
              {t(`vertical.${v.key}.label`, v.label)}
            </Link>
          ))}
        </div>
      </section>

      <section id="how" className="rms-landing-section">
        <h2 className="rms-page-title" style={{ textAlign: "center", marginBottom: 8 }}>
          {t("landing.loop.title", "Every rental business runs the same loop")}
        </h2>
        <p className="rms-page-sub" style={{ textAlign: "center", maxWidth: 560, margin: "0 auto 32px" }}>
          {t(
            "landing.loop.body",
            "Reserve → check out → track usage and condition → check in → invoice. We built that loop once and taught it your vocabulary."
          )}
        </p>
        <div className="rms-feature-grid">
          {FEATURES.map((f) => (
            <div key={f.key} className="rms-card rms-card-pad rms-feature">
              <h3>{t(`landing.feature.${f.key}.title`, f.title)}</h3>
              <p>{t(`landing.feature.${f.key}.body`, f.body)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rms-landing-section" style={{ background: "var(--rms-subtle)", maxWidth: "none" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2 className="rms-page-title" style={{ textAlign: "center", marginBottom: 8 }}>
            {t("landing.ads.title", "Free because advertisers pay, not you")}
          </h2>
          <p className="rms-page-sub" style={{ textAlign: "center", maxWidth: 560, margin: "0 auto 32px" }}>
            {t(
              "landing.ads.body",
              "The free plan shows relevant trade ads in the sidebar — insurance, parts, suppliers for your industry. Never during checkout, payment or an inspection. If you'd rather not see them, turn them off for a flat monthly fee."
            )}
          </p>
          <div className="rms-price-grid">
            <div className="rms-price-card">
              <strong style={{ fontSize: 14 }}>{t("plan.freeName", "Free")}</strong>
              <div className="rms-price-amount">$0</div>
              <div style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>
                {t("landing.plan.freeTag", "forever, all features")}
              </div>
              <ul className="rms-price-list">
                {FREE_PLAN.map((line, i) => (
                  <li key={i}>{t(`landing.plan.free.${i}`, line)}</li>
                ))}
              </ul>
            </div>
            <div className="rms-price-card is-featured">
              <strong style={{ fontSize: 14 }}>{t("plan.adFreeName", "Ad-Free")}</strong>
              <div className="rms-price-amount">$29</div>
              <div style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>
                {t("landing.plan.adFreeTag", "per month, per business")}
              </div>
              <ul className="rms-price-list">
                {AD_FREE_PLAN.map((line, i) => (
                  <li key={i}>{t(`landing.plan.adFree.${i}`, line)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="rms-landing-section" style={{ textAlign: "center" }}>
        <h2 className="rms-page-title" style={{ marginBottom: 10 }}>
          {t("landing.final.title", "Pick your business type and start")}
        </h2>
        <p className="rms-page-sub" style={{ marginBottom: 22 }}>
          {t("landing.final.body", "We'll configure your system around it before you add a single record.")}
        </p>
        {/* The same two words as the header and the hero. Three names for one
            action only ever taught visitors that they were three actions. */}
        <Link className="rms-btn rms-btn--primary" to="/rms/start" style={{ padding: "11px 22px" }}>
          {t("landing.startFree", "Start free")}
        </Link>
      </section>

      <footer style={{ borderTop: "1px solid var(--rms-border)", padding: "22px 24px", textAlign: "center", fontSize: 12.5, color: "var(--rms-muted)" }}>
        {t("landing.footer", "ReserveZoo — rental management for every trade.")}
      </footer>
    </div>
  );
}

export default Landing;
